import React, { useState, useEffect } from "react";
import { COSMETIC_ITEMS, getRarityLabel, getRarityColor } from "../data/cosmeticItems";
import { getDailyRotation, getPieceStyle } from "../data/shopItems";
import { getUserPurchases, getOwnedCosmetics } from "../lib/api";
import CrownCoin from "./CrownCoin";
import ShopPreviewModal  from "./ShopPreviewModal";
import CrownPurchaseModal from "./CrownPurchaseModal";
import { DS } from "../theme";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getCountdown() {
  const now = new Date();
  const midnight = new Date(now); midnight.setHours(24, 0, 0, 0);
  const ms = midnight - now;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${h}h ${m}m ${s}s`;
}

const CENTS_TO_CROWNS = { 149: 500, 199: 700, 249: 900, 399: 1400, 499: 1750, 599: 2100, 699: 2500 };

// Normalize standard shop items into the crown pricing system
function normalizeStripeItem(item) {
  return {
    ...item,
    priceType: "crowns",
    price: CENTS_TO_CROWNS[item.price] ?? Math.round(item.price * 3),
    tabCategory: item.category === "palette" || item.category === "design" ? "pieces" : "board",
    isStandardItem: true,
  };
}

// ── Tab config ────────────────────────────────────────────────────────────────

const TABS = [
  { id: "all",     label: "All"     },
  { id: "boards",  label: "Boards"  },
  { id: "pieces",  label: "Pieces"  },
  { id: "effects", label: "Effects" },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function Shop({ prefs, user, crowns = 0, onCrownsUpdate }) {
  const [activeTab, setActiveTab]   = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCrownModal, setShowCrownModal] = useState(false);
  const [stripeOwnedIds, setStripeOwnedIds]   = useState([]);   // from purchases table
  const [crownOwnedIds, setCrownOwnedIds]     = useState([]);   // from owned_cosmetics table
  const [countdown, setCountdown] = useState(getCountdown());

  const rotation = getDailyRotation(5).map(normalizeStripeItem);

  useEffect(() => {
    if (!user?.token) return;
    Promise.all([
      getUserPurchases(user.token).catch(() => null),
      getOwnedCosmetics(user.token).catch(() => null),
    ]).then(([purchases, owned]) => {
      if (purchases?.purchases) setStripeOwnedIds(purchases.purchases.map(p => p.itemId));
      if (owned) setCrownOwnedIds(owned.map(c => c.itemId));
    });
  }, [user]);

  useEffect(() => {
    const id = setInterval(() => setCountdown(getCountdown()), 1000);
    return () => clearInterval(id);
  }, []);

  function isOwned(id) {
    return stripeOwnedIds.includes(id) || crownOwnedIds.includes(id);
  }

  function handlePurchased(itemId, newCrowns) {
    if (newCrowns !== undefined) onCrownsUpdate?.(newCrowns);
    // Re-fetch both ownership lists so badges and Armoury reflect reality
    Promise.all([
      getUserPurchases(user.token).catch(() => null),
      getOwnedCosmetics(user.token).catch(() => null),
    ]).then(([purchases, owned]) => {
      if (purchases?.purchases) setStripeOwnedIds(purchases.purchases.map(p => p.itemId));
      if (owned) setCrownOwnedIds(owned.map(c => c.itemId));
    });
  }

  // ── Item sets per tab ────────────────────────────────────────────────────────

  const crownItems  = COSMETIC_ITEMS.map(c => ({ ...c, priceType: "crowns", tabCategory: c.category }));
  const featuredItem = crownItems.find(c => c.featured);

  function getDisplayItems() {
    if (activeTab === "all") return null; // "all" renders sections manually
    if (activeTab === "effects") {
      return crownItems.filter(c => c.tabCategory === "board");
    }
    const cat = activeTab === "boards" ? "board" : "pieces";
    return [
      ...crownItems.filter(c => c.tabCategory === cat),
      ...rotation.filter(r => r.tabCategory === cat),
    ];
  }

  const displayItems = getDisplayItems();

  return (
    <div>
      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 20,
      }}>
        <div>
          <div className="cinzel" style={{ fontSize: 14, fontWeight: 700, color: DS.text, letterSpacing: "0.06em" }}>
            Shop
          </div>
          <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 2 }}>
            Cosmetics · Boards · Pieces
          </div>
        </div>

        {/* Crown balance chip + buy button */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.28)",
            borderRadius: 8, padding: "6px 12px",
          }}>
            <CrownCoin size={16} shimmer />
            <span className="cinzel" style={{ fontSize: 14, fontWeight: 700, color: "#D4AF37" }}>
              {crowns.toLocaleString()}
            </span>
          </div>
          <button
            className="btn btn-gold btn-sm cinzel"
            onClick={() => setShowCrownModal(true)}
            title="Buy Crowns"
            style={{ padding: "6px 10px", fontSize: 14 }}
          >
            +
          </button>
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${DS.borderFaint}`, marginBottom: 20 }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`nav-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
            style={{ marginRight: 20 }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── All tab ─────────────────────────────────────────────────────────── */}
      {activeTab === "all" && (
        <div>
          {/* Featured */}
          {featuredItem && (
            <FeaturedCard
              item={featuredItem}
              isOwned={isOwned(featuredItem.id)}
              onClick={() => setSelectedItem(featuredItem)}
            />
          )}

          {/* Premium cosmetics (2-col grid, skip featured) */}
          <SectionHeader title="Premium" subtitle="Crown-powered cosmetics" />
          <ItemGrid
            items={crownItems.filter(c => !c.featured)}
            isOwned={isOwned}
            onSelect={setSelectedItem}
          />

          {/* Daily rotation */}
          <SectionHeader
            title="Daily Rotation"
            subtitle={`Resets in ${countdown}`}
            muted
          />
          <ItemGrid
            items={rotation}
            isOwned={isOwned}
            onSelect={setSelectedItem}
          />
        </div>
      )}

      {/* ── Boards / Pieces tabs ─────────────────────────────────────────────── */}
      {activeTab !== "all" && displayItems && (
        <ItemGrid items={displayItems} isOwned={isOwned} onSelect={setSelectedItem} />
      )}

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {selectedItem && (
        <ShopPreviewModal
          item={selectedItem}
          isOwned={isOwned(selectedItem.id)}
          crowns={crowns}
          user={user}
          onClose={() => setSelectedItem(null)}
          onPurchased={handlePurchased}
        />
      )}
      {showCrownModal && (
        <CrownPurchaseModal
          user={user}
          crowns={crowns}
          onClose={() => setShowCrownModal(false)}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle, muted = false }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, marginTop: 24 }}>
      <span className="cinzel" style={{
        fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
        color: muted ? DS.textFaint : DS.textMuted,
        textTransform: "uppercase",
      }}>
        {title}
      </span>
      {subtitle && (
        <span style={{ fontSize: 10, color: DS.textFaint }}>{subtitle}</span>
      )}
    </div>
  );
}

function FeaturedCard({ item, isOwned, onClick }) {
  const rarityColor = item.rarity ? getRarityColor(item.rarity) : DS.accent;

  return (
    <div
      onClick={onClick}
      style={{
        background: `linear-gradient(135deg, rgba(147,130,255,0.1), rgba(237,187,74,0.06))`,
        border: `1px solid ${rarityColor}35`,
        borderRadius: DS.radius,
        padding: "20px",
        cursor: "pointer",
        transition: "all 0.18s",
        display: "flex", gap: 18, alignItems: "center",
        marginBottom: 4,
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${rarityColor}60`;
        e.currentTarget.style.boxShadow = `0 0 24px ${rarityColor}18`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = `${rarityColor}35`;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Featured badge */}
      <div style={{
        position: "absolute", top: 10, left: 10,
        fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
        color: rarityColor, background: `${rarityColor}15`,
        border: `1px solid ${rarityColor}30`, borderRadius: 4, padding: "2px 7px",
      }}>
        Featured
      </div>

      {/* Piece preview */}
      <div style={{
        width: 80, height: 80, flexShrink: 0,
        background: "linear-gradient(135deg, #1A1830, #0D0C18)",
        borderRadius: 10, border: `1px solid ${rarityColor}25`,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 2,
        marginTop: 12,
      }}>
        <span style={{
          fontSize: 32, lineHeight: 1,
          color: item.preview?.pieceColor || "#C8B8FF",
          textShadow: item.preview?.glowColor
            ? `0 0 12px ${item.preview.glowColor}, 0 0 24px ${item.preview.glowColor}`
            : undefined,
        }}>♔</span>
        <span style={{
          fontSize: 28, lineHeight: 1,
          color: item.preview?.darkColor || "#2A1E5C",
          textShadow: item.preview?.glowColor
            ? `0 0 10px ${item.preview.glowColor}80`
            : undefined,
        }}>♚</span>
      </div>

      {/* Info */}
      <div style={{ flex: 1, marginTop: 10 }}>
        <div className="cinzel" style={{ fontSize: 15, fontWeight: 700, color: DS.text, marginBottom: 4 }}>
          {item.name}
        </div>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          marginBottom: 8,
          fontSize: 9, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
          color: rarityColor, border: `1px solid ${rarityColor}35`,
          borderRadius: 4, padding: "2px 7px",
        }}>
          ● {getRarityLabel(item.rarity)}
        </div>
        <div style={{ fontSize: 11, color: DS.textMuted, lineHeight: 1.5, marginBottom: 10 }}>
          {item.desc}
        </div>
        <div className="cinzel" style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 15, fontWeight: 700, color: "#D4AF37" }}>
          <CrownCoin size={14} /> {item.price.toLocaleString()}
          {isOwned && (
            <span style={{ fontSize: 10, color: DS.accent, marginLeft: 8, fontWeight: 600 }}>✓ Owned</span>
          )}
        </div>
      </div>

      {/* Arrow */}
      <div style={{ fontSize: 16, color: DS.textFaint, flexShrink: 0 }}>›</div>
    </div>
  );
}

function ItemGrid({ items, isOwned, onSelect }) {
  if (!items.length) {
    return (
      <div style={{ color: DS.textFaint, fontSize: 12, padding: "16px 0" }}>
        Nothing here yet.
      </div>
    );
  }
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 10,
    }}>
      {items.map(item => (
        <ItemCard
          key={item.id}
          item={item}
          owned={isOwned(item.id)}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function ItemCard({ item, owned, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const isCrown    = item.priceType === "crowns";
  const rarityColor = item.rarity ? getRarityColor(item.rarity) : null;
  const borderColor = hovered
    ? (rarityColor ? `${rarityColor}50` : `${DS.accent}40`)
    : (owned ? `${DS.accent}28` : DS.borderFaint);

  return (
    <div
      onClick={() => onSelect(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: DS.surface,
        border: `1px solid ${borderColor}`,
        borderRadius: DS.radiusSm,
        padding: "14px 12px",
        cursor: "pointer",
        transition: "all 0.15s",
        transform: hovered ? "translateY(-1px)" : "none",
        boxShadow: hovered ? `0 4px 16px rgba(0,0,0,0.3)` : "none",
        display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      {/* Preview thumbnail */}
      <ItemThumb item={item} />

      {/* Info */}
      <div>
        <div className="cinzel" style={{ fontSize: 11, fontWeight: 700, color: DS.text, marginBottom: 4, lineHeight: 1.2 }}>
          {item.name}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 4 }}>
          {/* Price */}
          <div className="cinzel" style={{
            fontSize: 12, fontWeight: 700,
            color: isCrown ? "#D4AF37" : DS.gold,
            display: "flex", alignItems: "center", gap: 4,
          }}>
            {isCrown ? (
              <><CrownCoin size={11} /> {item.price.toLocaleString()}</>
            ) : (
              `$${(item.price / 100).toFixed(2)}`
            )}
          </div>

          {/* Rarity or category badge */}
          {item.rarity ? (
            <span style={{
              fontSize: 8, fontWeight: 700, letterSpacing: "0.06em",
              textTransform: "uppercase", borderRadius: 3, padding: "1px 5px",
              color: rarityColor, border: `1px solid ${rarityColor}35`,
            }}>
              {getRarityLabel(item.rarity)}
            </span>
          ) : (
            <span style={{
              fontSize: 8, color: DS.textFaint, textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}>
              {{ board: "Board", palette: "Palette", design: "Design", pieces: "Pieces" }[item.category] || item.category}
            </span>
          )}
        </div>
      </div>

      {owned && (
        <div style={{
          fontSize: 9, color: DS.accent, fontWeight: 700, letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}>
          ✓ Owned
        </div>
      )}
    </div>
  );
}

function ItemThumb({ item }) {
  const SZ = 44;

  if (item.category === "board") {
    const light = item.preview?.lightSquare || item.light || "#F0D9B5";
    const dark  = item.preview?.darkSquare  || item.dark  || "#B58863";
    return (
      <div style={{ width: "100%", height: SZ, borderRadius: 6, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", height: "100%" }}>
          {Array.from({ length: 16 }, (_, i) => {
            const r = Math.floor(i / 4), c = i % 4;
            return <div key={i} style={{ background: (r + c) % 2 === 0 ? light : dark }} />;
          })}
        </div>
      </div>
    );
  }

  if (item.category === "pieces") {
    return (
      <div style={{
        width: "100%", height: SZ, borderRadius: 6, overflow: "hidden",
        background: "linear-gradient(135deg, #B58863 50%, #F0D9B5 50%)",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
      }}>
        <span style={{
          fontSize: 24, lineHeight: 1,
          color: item.preview?.pieceColor || "#F0EEF8",
          textShadow: item.preview?.glowColor ? `0 0 10px ${item.preview.glowColor}` : undefined,
        }}>♔</span>
        <span style={{
          fontSize: 24, lineHeight: 1,
          color: item.preview?.darkColor || "#1C1A2E",
        }}>♚</span>
      </div>
    );
  }

  if (item.category === "palette") {
    const wStyle = getPieceStyle("classic", item.id, true);
    const bStyle = getPieceStyle("classic", item.id, false);
    return (
      <div style={{
        width: "100%", height: SZ, borderRadius: 6, overflow: "hidden",
        background: "linear-gradient(135deg, #B58863 50%, #F0D9B5 50%)",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
      }}>
        <span style={{ fontSize: 24, lineHeight: 1, ...wStyle }}>♔</span>
        <span style={{ fontSize: 24, lineHeight: 1, ...bStyle }}>♚</span>
      </div>
    );
  }

  if (item.category === "design") {
    const wStyle = getPieceStyle(item.id, "classic", true);
    const bStyle = getPieceStyle(item.id, "classic", false);
    return (
      <div style={{
        width: "100%", height: SZ, borderRadius: 6, overflow: "hidden",
        background: "linear-gradient(135deg, #B58863 50%, #F0D9B5 50%)",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
      }}>
        <span style={{ fontSize: 24, lineHeight: 1, ...wStyle }}>♔</span>
        <span style={{ fontSize: 24, lineHeight: 1, ...bStyle }}>♚</span>
      </div>
    );
  }

  return <div style={{ width: "100%", height: SZ, background: DS.elevated, borderRadius: 6 }} />;
}
